import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAlertsHistory, fetchAlertStatistics, fetchAllAlertTypes, resolveAlertByHistoryId, resolveAlerts } from '../services/api';
import { useSites } from '../context/SitesContext';
import { useAuth } from '../context/AuthContext';
import MessageModal from '../components/MessageModal';
import SearchableSelect from '../components/SearchableSelect';
import './AlertsHistory.css';
import { formatDateTime } from '../utils/dateFormat';

const AlertsHistory = () => {
  const navigate = useNavigate();
  const { sites } = useSites();
  const { authData } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [allAlertTypes, setAllAlertTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAlerts, setSelectedAlerts] = useState(new Set());
  const [resolving, setResolving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [messageModal, setMessageModal] = useState({ show: false, message: '', type: 'info' });
  
  // Filters
  const [filters, setFilters] = useState({
    siteId: '',
    type: '',
    severity: '',
    resolved: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  
  // Sorting state
  const [sortField, setSortField] = useState('timestamp'); // Default sort by timestamp
  const [sortDirection, setSortDirection] = useState('desc'); // Default descending (newest first)

  const loadAlerts = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const filterParams = {
        ...filters,
        siteId: filters.siteId ? parseInt(filters.siteId) : undefined,
        resolved: filters.resolved !== '' ? filters.resolved === 'true' : undefined,
        sortField: sortField,
        sortDirection: sortDirection,
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage
      };
      
      // Remove empty filters
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] === '' || filterParams[key] === undefined) {
          delete filterParams[key];
        }
      });
      
      const [alertsData, statsData] = await Promise.all([
        fetchAlertsHistory(filterParams),
        fetchAlertStatistics(filterParams)
      ]);
      
      setAlerts(alertsData);
      setStats(statsData);
      // Clear selection when data changes
      // Only clear if this is not a refresh (to preserve user selection during background updates)
      if (!isRefresh) {
        setSelectedAlerts(new Set());
      }
    } catch (err) {
      setError('Failed to load alerts history');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    await loadAlerts(true);
  };

  const loadAllAlertTypes = async () => {
    try {
      const types = await fetchAllAlertTypes();
      setAllAlertTypes(types);
    } catch (err) {
      console.error('Failed to load alert types:', err);
      // Don't set error state, just log it - we can still show the dropdown with current page types
    }
  };

  useEffect(() => {
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    // Load all alert types once on component mount
    loadAllAlertTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    // Reset to page 1 when sorting changes and reload
    setCurrentPage(1);
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      siteId: '',
      type: '',
      severity: '',
      resolved: '',
      search: '',
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
  };

  const handleResolveAlert = async (alertId) => {
    try {
      const userName = authData?.userName || null;
      // Use resolveAlertByHistoryId since alertId here is actually a history ID
      await resolveAlertByHistoryId(alertId, userName);
      await loadAlerts();
      setMessageModal({ show: true, message: 'Alert resolved successfully', type: 'success' });
    } catch (err) {
      console.error('Failed to resolve alert:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to resolve alert';
      setMessageModal({ show: true, message: errorMessage, type: 'error' });
    }
  };

  const handleSelectAlert = (alertId) => {
    setSelectedAlerts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    // Only select unresolved alerts
    const unresolvedAlerts = alerts.filter(alert => !alert.resolved);
    if (selectedAlerts.size === unresolvedAlerts.length) {
      // Deselect all
      setSelectedAlerts(new Set());
    } else {
      // Select all unresolved
      setSelectedAlerts(new Set(unresolvedAlerts.map(alert => alert.id)));
    }
  };

  const handleResolveSelected = async () => {
    if (selectedAlerts.size === 0) return;
    
    try {
      setResolving(true);
      const alertIds = Array.from(selectedAlerts);
      const userName = authData?.userName || null;
      const result = await resolveAlerts(alertIds, userName);
      
      if (result.resolved > 0) {
        await loadAlerts();
        setMessageModal({ 
          show: true, 
          message: `Successfully resolved ${result.resolved} alert(s)`, 
          type: 'success' 
        });
      } else {
        await loadAlerts(); // Refresh to update the UI
        setMessageModal({ 
          show: true, 
          message: 'No alerts were resolved. They may have already been resolved or do not exist.', 
          type: 'warning' 
        });
      }
    } catch (err) {
      console.error('Failed to resolve alerts:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to resolve selected alerts';
      setMessageModal({ 
        show: true, 
        message: `Failed to resolve selected alerts: ${errorMessage}`, 
        type: 'error' 
      });
    } finally {
      setResolving(false);
    }
  };


  const getSeverityColor = (severity) => {
    return severity === 'critical' ? '#dc3545' : '#ffc107';
  };

  const getTypeDisplayName = (type) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Use all alert types from database, fallback to current page types if not loaded yet
  const alertTypes = allAlertTypes.length > 0 
    ? allAlertTypes 
    : [...new Set(alerts.map(a => a.type))].sort();

  const totalPages = Math.ceil((stats?.total || 0) / itemsPerPage);
  
  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get unresolved alerts for select all (no need to sort since sorting is done server-side)
  const unresolvedAlerts = alerts.filter(alert => !alert.resolved);
  const allUnresolvedSelected = unresolvedAlerts.length > 0 && 
    unresolvedAlerts.every(alert => selectedAlerts.has(alert.id));
  
  // Helper function to render sort indicator
  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <span className="sort-indicator">↕</span>;
    }
    return sortDirection === 'asc' ? 
      <span className="sort-indicator sort-asc">↑</span> : 
      <span className="sort-indicator sort-desc">↓</span>;
  };

  return (
    <div className="alerts-history">
      <div className="alerts-history-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back to Dashboard
        </button>
        <h1>Alerts History</h1>
        <button 
          className="refresh-btn" 
          onClick={handleRefresh} 
          disabled={refreshing || loading}
          title="Refresh Alerts"
        >
          {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="alerts-stats">
          <div className="stat-card">
            <div className="stat-label">Total Alerts</div>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="stat-card critical">
            <div className="stat-label">Critical</div>
            <div className="stat-value">{stats.critical}</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-label">Warning</div>
            <div className="stat-value">{stats.warning}</div>
          </div>
          <div className="stat-card resolved">
            <div className="stat-label">Resolved</div>
            <div className="stat-value">{stats.resolved}</div>
          </div>
          <div className="stat-card unresolved">
            <div className="stat-label">Unresolved</div>
            <div className="stat-value">{stats.unresolved}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="alerts-filters">
        <h2>Filters</h2>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Site</label>
            <SearchableSelect
              value={filters.siteId || ''}
              onChange={(value) => handleFilterChange('siteId', value || '')}
              options={[
                { id: '', name: 'All Sites' },
                ...sites.map(site => ({
                  id: site.id,
                  name: `${site.name} (${site.ip})`
                }))
              ]}
              placeholder="Search sites..."
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
            />
          </div>

          <div className="filter-group">
            <label>Type</label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="">All Types</option>
              {alertTypes.map(type => (
                <option key={type} value={type}>
                  {getTypeDisplayName(type)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Severity</label>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.resolved}
              onChange={(e) => handleFilterChange('resolved', e.target.value)}
            >
              <option value="">All</option>
              <option value="false">Unresolved</option>
              <option value="true">Resolved</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search in messages, type, or site..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <button className="clear-filters-btn" onClick={handleClearFilters}>
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Alerts Table */}
      <div className="alerts-table-container">
        {loading ? (
          <div className="loading">Loading alerts...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : alerts.length === 0 ? (
          <div className="no-alerts">No alerts found matching your filters.</div>
        ) : (
          <>
            {/* Bulk Actions Bar */}
            {selectedAlerts.size > 0 && (
              <div className="bulk-actions-bar">
                <span className="selected-count">
                  {selectedAlerts.size} alert{selectedAlerts.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  className="resolve-selected-btn"
                  onClick={handleResolveSelected}
                  disabled={resolving}
                >
                  {resolving ? 'Resolving...' : `Resolve Selected (${selectedAlerts.size})`}
                </button>
              </div>
            )}
            
            <table className="alerts-table">
              <thead>
                <tr>
                  <th className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={allUnresolvedSelected}
                      onChange={handleSelectAll}
                      title="Select all unresolved alerts"
                    />
                  </th>
                  <th className="sortable" onClick={() => handleSort('timestamp')}>
                    Timestamp {renderSortIndicator('timestamp')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('site')}>
                    Site {renderSortIndicator('site')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('type')}>
                    Type {renderSortIndicator('type')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('message')}>
                    Message {renderSortIndicator('message')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('severity')}>
                    Severity {renderSortIndicator('severity')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('device')}>
                    Device {renderSortIndicator('device')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('status')}>
                    Status {renderSortIndicator('status')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('resolvedBy')}>
                    Resolved By {renderSortIndicator('resolvedBy')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map(alert => (
                  <tr key={alert.id} className={alert.resolved ? 'resolved' : ''}>
                    <td className="checkbox-column">
                      {!alert.resolved && (
                        <input
                          type="checkbox"
                          checked={selectedAlerts.has(alert.id)}
                          onChange={() => handleSelectAlert(alert.id)}
                        />
                      )}
                    </td>
                    <td>{formatDateTime(alert.timestamp)}</td>
                    <td>
                      <div className="site-info">
                        <div 
                          className="site-name site-name-link" 
                          onClick={() => navigate(`/site/${alert.siteId}`)}
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#3b82f6' }}
                          title="Click to view site details"
                        >
                          {alert.siteName || `Site ${alert.siteId}`}
                        </div>
                        {alert.siteIp && (
                          <div className="site-ip">{alert.siteIp}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="alert-type">{getTypeDisplayName(alert.type)}</span>
                    </td>
                    <td className="message-cell">{alert.message}</td>
                    <td>
                      <span
                        className="severity-badge"
                        style={{ backgroundColor: getSeverityColor(alert.severity) }}
                      >
                        {alert.severity.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      {alert.deviceName || alert.deviceId || 'N/A'}
                    </td>
                    <td>
                      {alert.resolved ? (
                        <span className="status-badge resolved-badge">
                          Resolved
                          {alert.resolvedAt && (
                            <div className="resolved-date">{formatDateTime(alert.resolvedAt)}</div>
                          )}
                        </span>
                      ) : (
                        <span className="status-badge unresolved-badge">Unresolved</span>
                      )}
                    </td>
                    <td>{alert.resolvedBy || 'N/A'}</td>
                    <td>
                      {!alert.resolved && (
                        <button
                          className="resolve-btn"
                          onClick={() => handleResolveAlert(alert.id)}
                        >
                          Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Message Modal */}
      <MessageModal
        show={messageModal.show}
        message={messageModal.message}
        type={messageModal.type}
        onClose={() => setMessageModal({ show: false, message: '', type: 'info' })}
      />
    </div>
  );
};

export default AlertsHistory;

